import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Legend, AreaChart, Area, LabelList, ComposedChart,
} from "recharts";

const PALETTE = ["#004182", "#F59E0B", "#0e7490", "#be185d", "#15803d", "#7c3aed"];

function computeYDomain(data, keys) {
  let min = Infinity, max = -Infinity;
  (data || []).forEach((row) => keys.forEach((k) => {
    const v = Number(row[k]);
    if (!Number.isNaN(v)) { min = Math.min(min, v); max = Math.max(max, v); }
  }));
  if (min === Infinity) return ["auto", "auto"];
  return [Math.floor(min) - 1, Math.ceil(max) + 1];
}

function ChartCardImpl({ chart, index, light = true, wide = false }) {
  const data = chart.data || [];
  const axisColor = light ? "#64748b" : "#94a3b8";
  const gridColor = light ? "#e8edf3" : "rgba(255,255,255,0.08)";
  const height = wide ? 280 : 200;

  if (chart.type === "combo") {
    const bars = (chart.series || []).filter((s) => s.chartType === "bar");
    const lines = (chart.series || []).filter((s) => s.chartType === "line");
    return (
      <div className={`rounded-2xl p-4 ${light ? "bg-white border border-slate-200" : "panel"}`} data-testid={`chart-${chart.id}`}>
        <div className="text-center text-[#004182] font-bold text-sm mb-2">{chart.title}</div>
        <ResponsiveContainer width="100%" height={wide ? 340 : 260}>
          <ComposedChart data={data} margin={{ top: 22, right: 6, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: axisColor }} interval={0} angle={-45} textAnchor="end" height={54} />
            <YAxis yAxisId="left" tick={{ fontSize: 9, fill: axisColor }} width={54} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: axisColor }} width={44} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {bars.map((b) => (
              <Bar key={b.key} yAxisId={b.axis === "right" ? "right" : "left"} dataKey={b.key} name={b.name} fill={b.color} radius={[2, 2, 0, 0]} maxBarSize={26} />
            ))}
            {lines.map((l) => (
              <Line key={l.key} yAxisId={l.axis === "right" ? "right" : "left"} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2} dot={{ r: 2 }} activeDot={false}>
                <LabelList dataKey={l.key} position="top" angle={-90} offset={12} style={{ fontSize: 8, fill: "#334155", fontWeight: 600 }} />
              </Line>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
        {chart.note && <div className="text-[11px] italic text-slate-500 mt-1">{chart.note}</div>}
      </div>
    );
  }

  const multi = Array.isArray(chart.series) && chart.series.length > 0;
  const seriesDefs = multi
    ? chart.series.map((s, i) => ({ key: s.key, name: s.name || s.key, color: s.color || PALETTE[i % PALETTE.length] }))
    : [{ key: "value", name: chart.series_name || "Value", color: PALETTE[index % PALETTE.length] }];
  const yDomain = computeYDomain(data, seriesDefs.map((s) => s.key));
  const labelStyle = { fontSize: 10, fill: light ? "#334155" : "#e2e8f0", fontWeight: 600 };

  const commonAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 10, fill: axisColor }} interval={0} angle={-22} textAnchor="end" height={46} />
      <YAxis tick={{ fontSize: 10, fill: axisColor }} domain={yDomain} allowDecimals={true} />
      {multi && <Legend wrapperStyle={{ fontSize: 11 }} />}
    </>
  );

  return (
    <div className={`rounded-2xl p-4 ${light ? "bg-white border border-slate-200" : "panel"}`} data-testid={`chart-${chart.id}`}>
      <div className={`text-sm font-semibold ${light ? "text-slate-800" : "text-slate-100"}`}>{chart.title}</div>
      <div className="text-[11px] mb-2" style={{ color: axisColor }}>{chart.unit}</div>
      <ResponsiveContainer width="100%" height={height}>
        {chart.type === "bar" ? (
          <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            {commonAxes}
            {seriesDefs.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]}>
                <LabelList dataKey={s.key} position="top" style={labelStyle} />
              </Bar>
            ))}
          </BarChart>
        ) : multi ? (
          <LineChart data={data} margin={{ top: 18, right: 10, left: -16, bottom: 0 }}>
            {commonAxes}
            {seriesDefs.map((s) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2.4} dot={{ r: 2 }} activeDot={false}>
                <LabelList dataKey={s.key} position="top" style={{ ...labelStyle, fill: s.color }} />
              </Line>
            ))}
          </LineChart>
        ) : (
          <AreaChart data={data} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${chart.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={seriesDefs[0].color} stopOpacity={0.32} />
                <stop offset="100%" stopColor={seriesDefs[0].color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            {commonAxes}
            <Area type="monotone" dataKey="value" name={seriesDefs[0].name} stroke={seriesDefs[0].color} strokeWidth={2.6} fill={`url(#grad-${chart.id})`} activeDot={false}>
              <LabelList dataKey="value" position="top" style={labelStyle} />
            </Area>
          </AreaChart>
        )}
      </ResponsiveContainer>
      {chart.show_table && <ChartDataTable data={data} seriesDefs={seriesDefs} />}
    </div>
  );
}

function ChartDataTable({ data, seriesDefs }) {
  return (
    <div className="overflow-x-auto mt-3" data-testid="chart-data-table">
      <table className="border-collapse text-[11px] w-full">
        <thead>
          <tr>
            <th className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-left"></th>
            {data.map((r, i) => <th key={i} className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-slate-600 whitespace-nowrap">{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {seriesDefs.map((s) => (
            <tr key={s.key}>
              <td className="border border-slate-200 px-1.5 py-1 font-medium whitespace-nowrap" style={{ color: s.color }}>{s.name}</td>
              {data.map((r, i) => <td key={i} className="border border-slate-200 px-1.5 py-1 text-slate-700 text-center">{r[s.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChartCard({ chart, index = 0, light = true, wide = false }) {
  return <ChartCardImpl chart={chart} index={index} light={light} wide={wide} />;
}

export default function ReportCharts({ charts, light = true }) {
  if (!charts || charts.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="report-charts">
      {charts.map((c, i) => <ChartCardImpl key={c.id || i} chart={c} index={i} light={light} />)}
    </div>
  );
}
